// Pieces of a Penyedia paket shared by the owner's project page
// (./Paket.tsx) and the konsultan's read-only view (screens/Pendampingan.tsx):
// the paket header (name, instansi, HPS in Rupiah, deadline countdown, the
// LPSE link, and the announcement details once filled: metode, jenis,
// kualifikasi, pagu, kode paket, kode RUP, status paket, PIC, lokasi, durasi,
// and the ringkasan KAK folded under them), the stage stepper (four
// stages; on a narrow frame a compact "Tahap 2 dari 4"), a step's detail
// (tindakan, output bukti, risiko, dasar hukum with its link), a read-only
// checklist, and a consultation's notes.
import type { ReactNode } from "react";
import type { ChecklistGroup, Consultation, PaketStage, Project, SupplierStepDetail } from "../api";
import { formatDateTime, formatRupiah } from "../lib/format";
import { Badge } from "../ui/components";
import { Glyph } from "../ui/icons";
import { JadwalTable, isRunning, jakartaToday } from "./PaketJadwal";
import {
  CONSULT_STATUS_LABEL,
  FIRST_STAGE,
  JENIS_LABEL,
  KUALIFIKASI_LABEL,
  METODE_LABEL,
  STAGE_LABEL,
  STEPS,
  consultTone,
  countdownText,
  daysUntil,
  formatDeadline,
  paketTitle,
  stepNumber,
} from "./stages";

export interface PaketHeaderProps {
  project: Project;
  /** The pencil on the info box (Ubah info paket); none for a konsultan. */
  onEdit?: () => void;
  /** The row's trailing control (the owner's Menu); none for a konsultan. */
  action?: ReactNode;
  /** The page's h1 (the owner's page and the konsultan view both have one). */
  headingId?: string;
  /** Done and total checklist steps, for the Progres badge (Simulasi Pluto v4); none hides it. */
  progress?: { done: number; total: number };
}

/** The stage badge: Ditutup and Selesai read as outcomes, the rest as "Tahap X". */
export function StageBadge({ stage, outcome }: { stage: PaketStage; outcome: Project["outcome"] }) {
  if (stage === "ditutup") return <Badge tone="neutral">Ditutup, tidak terpilih</Badge>;
  if (stage === "selesai") return <Badge tone="success">Selesai</Badge>;
  if (outcome === "terpilih") return <Badge tone="success">Terpilih, {STAGE_LABEL[stage]}</Badge>;
  return <Badge tone="info">Tahap {STAGE_LABEL[stage]}</Badge>;
}

export function PaketHeader({ project, action, onEdit, headingId = "pk-title", progress }: PaketHeaderProps) {
  const stage = project.stage ?? FIRST_STAGE;
  const days = project.deadline ? daysUntil(project.deadline) : null;
  // The countdown matters while the offer is being prepared.
  const counting = days !== null && (stage === "persiapan_pemilihan" || stage === "pelaksanaan_pemilihan");
  // The announcement details, listed once filled.
  const extra: [string, string][] = [];
  const jadwal = project.jadwal ?? [];
  // The SPSE tahapan running today, from the schedule the intake read (migration 0045).
  const spseNow = jadwal.filter((j) => isRunning(j, jakartaToday())).at(-1);
  if (spseNow) extra.push(["Tahap SPSE", spseNow.tahapan]);
  if (project.metode) extra.push(["Metode", METODE_LABEL[project.metode] + (project.kualifikasi ? `, ${KUALIFIKASI_LABEL[project.kualifikasi]}` : "")]);
  if (project.jenisPengadaan) extra.push(["Jenis pengadaan", JENIS_LABEL[project.jenisPengadaan]]);
  if (project.pagu != null) extra.push(["Pagu", formatRupiah(project.pagu)]);
  if (project.kodePaket) extra.push(["Kode paket", project.kodePaket]);
  if (project.kodeRup) extra.push(["Kode RUP", project.kodeRup]);
  if (project.statusPaket) extra.push(["Status paket", project.statusPaket]);
  if (project.pic) extra.push(["PIC", project.pic]);
  if (project.lokasi) extra.push(["Lokasi", project.lokasi]);
  if (project.durasiHari != null) extra.push(["Durasi", `${project.durasiHari} hari`]);
  const kak = project.ringkasanKak?.trim() ?? "";
  return (
    <header className="pk-head" aria-labelledby={headingId}>
      <div className="pk-head-row">
        <p className="pk-kind">
          <Glyph name="box" size={18} />
          <span>Paket</span>
          <StageBadge stage={stage} outcome={project.outcome} />
        </p>
        {action}
      </div>
      <h1 id={headingId} className="pk-title">
        {paketTitle(project)}
      </h1>
      {project.paketName && project.paketName.trim() !== project.name ? <p className="pk-sub">Project: {project.name}</p> : null}
      <p className="pk-sub">
        {[project.instansi || "Instansi belum diisi", project.jenisPengadaan ? JENIS_LABEL[project.jenisPengadaan] : null, project.metode ? METODE_LABEL[project.metode] : null]
          .filter(Boolean)
          .join(" · ")}
      </p>
      <ul className="pk-badges" aria-label="Ringkasan paket">
        <li>
          <Badge tone="info">{project.kodePaket || project.kodeRup || "Kode paket belum terisi"}</Badge>
        </li>
        {project.tahunAnggaran ? (
          <li>
            <Badge tone="neutral">TA {project.tahunAnggaran}</Badge>
          </li>
        ) : null}
        <li>
          <Badge tone="neutral">Pagu {project.pagu == null ? "belum diisi" : formatRupiah(project.pagu)}</Badge>
        </li>
        {progress ? (
          <li>
            <Badge tone="neutral">Progres {progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0}%</Badge>
          </li>
        ) : null}
      </ul>
      <div className="pk-facts-box">
      {onEdit ? (
        <button type="button" className="ui-icon-btn pk-edit" aria-label="Ubah info paket" title="Ubah info paket" onClick={onEdit}>
          <Glyph name="edit2" />
        </button>
      ) : null}
      <dl className="pk-facts">
        <div className="pk-fact">
          <dt>Instansi</dt>
          <dd>{project.instansi || "Belum diisi"}</dd>
        </div>
        <div className="pk-fact">
          <dt>Nilai HPS</dt>
          <dd className="num">{project.nilaiHps == null ? "Belum diisi" : formatRupiah(project.nilaiHps)}</dd>
        </div>
        <div className="pk-fact">
          <dt>Batas penawaran</dt>
          <dd>
            {project.deadline ? (
              <>
                <span>
                  {formatDeadline(project.deadline)}
                  {project.deadlineTime ? `, ${project.deadlineTime}` : ""}
                </span>
                {counting ? (
                  <span className="pk-countdown" data-tone={days! < 0 ? "past" : days! <= 3 ? "soon" : undefined}>
                    <Glyph name="clock" size={16} />
                    {countdownText(days!)}
                  </span>
                ) : null}
              </>
            ) : (
              "Belum diisi"
            )}
          </dd>
        </div>
        {project.sumberUrl ? (
          <div className="pk-fact">
            <dt>LPSE</dt>
            <dd>
              <a className="pk-link" href={project.sumberUrl} target="_blank" rel="noopener noreferrer nofollow">
                <Glyph name="link" size={16} />
                <span>Buka pengumuman</span>
              </a>
            </dd>
          </div>
        ) : null}
        {extra.map(([label, value]) => (
          <div key={label} className="pk-fact">
            <dt>{label}</dt>
            <dd className={label === "Pagu" ? "num" : undefined}>{value}</dd>
          </div>
        ))}
      </dl>
      </div>
      {jadwal.length > 0 ? (
        <details className="pk-jadwal">
          <summary>Jadwal tahapan SPSE ({jadwal.length})</summary>
          <JadwalTable jadwal={jadwal} />
        </details>
      ) : null}
      {kak ? (
        // The KAK summary can run long: folded by default under the facts.
        <details className="pk-kak">
          <summary>
            <Glyph name="chevronRight" size={16} />
            <span>Ringkasan KAK</span>
          </summary>
          <p className="pk-kak-text">{kak}</p>
        </details>
      ) : null}
    </header>
  );
}

/** The stepper; `compact` always shows the "Tahap N dari 4" line (the conversation route). Selesai ticks every stage. */
export function StageStepper({ stage, compact = false }: { stage: PaketStage; compact?: boolean }) {
  const current = stepNumber(stage);
  const closed = stage === "ditutup";
  const finished = stage === "selesai";
  const shown = Math.min(current, STEPS.length);
  return (
    <div className="pk-stepper" data-compact={compact ? "" : undefined}>
      <p className="pk-step-compact" aria-hidden="true">
        <span className="pk-step-count num">
          Tahap {shown} dari {STEPS.length}
        </span>
        <span className="pk-step-name">{closed ? "Pelaksanaan Pemilihan, ditutup" : STAGE_LABEL[stage]}</span>
      </p>
      <span className="pk-step-bar" aria-hidden="true">
        <span className="pk-step-fill" style={{ width: `${(shown / STEPS.length) * 100}%` }} />
      </span>
      <ol className="pk-steps" aria-label={finished ? "Tahap paket: selesai" : `Tahap paket: ${current} dari ${STEPS.length}, ${STAGE_LABEL[stage]}`}>
        {STEPS.map((s, i) => {
          const n = i + 1;
          const state = n < current ? "done" : n === current ? (closed ? "closed" : "current") : closed ? "skipped" : "upcoming";
          return (
            <li key={s.stage} className="pk-step" data-state={state} aria-current={n === current ? "step" : undefined}>
              <span className="pk-step-dot" aria-hidden="true">
                {state === "done" ? <Glyph name="check" size={16} /> : state === "closed" ? <Glyph name="close" size={16} /> : <span className="num">{n}</span>}
              </span>
              <span className="pk-step-label">{s.label}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** Done count of a stage's items: "2 dari 6 selesai". */
export function progressText(items: { done: boolean }[]): string {
  return `${items.filter((i) => i.done).length} dari ${items.length} selesai`;
}

/** A step's detail from the supplier process database: tindakan, output bukti, risiko, dasar hukum with its link. */
export function StepDetail({ detail, id }: { detail: SupplierStepDetail; id?: string }) {
  return (
    <dl id={id} className="pk-detail">
      <div className="pk-detail-row">
        <dt>Tindakan</dt>
        <dd>{detail.tindakan}</dd>
      </div>
      <div className="pk-detail-row">
        <dt>Output bukti</dt>
        <dd>{detail.output}</dd>
      </div>
      <div className="pk-detail-row">
        <dt>Risiko dan titik keputusan</dt>
        <dd>{detail.risiko}</dd>
      </div>
      {detail.pihak ? (
        <div className="pk-detail-row">
          <dt>Pihak terkait</dt>
          <dd>{detail.pihak}</dd>
        </div>
      ) : null}
      <div className="pk-detail-row">
        <dt>Berlaku</dt>
        <dd>{detail.kondisi}</dd>
      </div>
      <div className="pk-detail-row">
        <dt>Dasar hukum</dt>
        <dd>
          <span>{detail.dasarHukum}</span>
          {detail.url ? (
            <a className="pk-link pk-detail-link" href={detail.url} target="_blank" rel="noopener noreferrer nofollow">
              <Glyph name="link" size={16} />
              <span>Buka rujukan</span>
            </a>
          ) : null}
        </dd>
      </div>
    </dl>
  );
}

/** The checklist read-only (konsultan): every stage, the current one first and open. */
export function ChecklistReadOnly({ groups, stage }: { groups: ChecklistGroup[]; stage: PaketStage }) {
  return (
    <div className="pk-ro">
      {groups.map((g) => (
        <section key={g.stage} className="pk-ro-group" data-current={g.stage === stage ? "" : undefined} aria-labelledby={`pk-ro-${g.stage}`}>
          <div className="pk-ro-head">
            <h3 id={`pk-ro-${g.stage}`} className="pk-ro-title">
              {STAGE_LABEL[g.stage]}
              {g.stage === stage ? <span className="pk-ro-now">Tahap saat ini</span> : null}
            </h3>
            <span className="pk-ro-count num">{progressText(g.items)}</span>
          </div>
          <ul className="pk-ro-list">
            {g.items.map((item) => (
              <li key={item.id} className="pk-ro-item" data-done={item.done ? "" : undefined}>
                <span className="pk-ro-mark" aria-hidden="true">
                  {item.done ? <Glyph name="tickCircle" size={18} /> : <span className="pk-ro-open" />}
                </span>
                <span className="pk-ro-text">
                  <span className="pk-ro-label">
                    {item.label}
                    <span className="visually-hidden">{item.done ? ", selesai" : ", belum selesai"}</span>
                  </span>
                  {item.note ? <span className="pk-ro-note">{item.note}</span> : null}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/** A request's status line: the badge and who it is with. */
export function ConsultationStatusLine({ consultation }: { consultation: Consultation }) {
  return (
    <span className="pd-status">
      <Badge tone={consultTone(consultation.status)}>{CONSULT_STATUS_LABEL[consultation.status]}</Badge>
      <span className="pd-status-text">
        {consultation.consultantName ? `Konsultan: ${consultation.consultantName}` : "Menunggu konsultan ditugaskan"}
      </span>
    </span>
  );
}

/** The notes of a request, oldest first. */
export function ConsultationNotes({ consultation, viewer }: { consultation: Consultation; viewer: "pemilik" | "konsultan" }) {
  if (consultation.notes.length === 0) {
    return <p className="pd-empty">{viewer === "pemilik" ? "Belum ada catatan dari konsultan." : "Belum ada catatan. Tulis catatan pertama untuk penyedia."}</p>;
  }
  return (
    <ol className="pd-notes" aria-label={`Catatan untuk ${consultation.topic}`}>
      {consultation.notes.map((n) => (
        <li key={n.id} className="pd-note" data-author={n.author}>
          <p className="pd-note-head">
            <span className="pd-note-name">{n.authorName}</span>
            <span className="pd-note-role">{n.author === "konsultan" ? "Konsultan" : "Penyedia"}</span>
            <span className="pd-note-time num">{formatDateTime(n.createdAt)}</span>
          </p>
          <p className="pd-note-body">{n.body}</p>
        </li>
      ))}
    </ol>
  );
}
